import { Button, TextInput } from "@components/library";
import { COLORS, FONTS, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@constants";
import { register } from "@requests/users";
import { useMutation } from "@tanstack/react-query";
import { identify, usedPersonalEmail } from "@utils/appUtils";
import { popSignupEntryCookie } from "@utils/authUtils";
import { AxiosError } from "axios";
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useState } from "react";
import styled from "styled-components";
import Validator from "validator";
import { LinkButton } from "./SignUpModal";

interface Props {
  setShouldShowSSO: Dispatch<SetStateAction<boolean>>;
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
}

const SignUpModalDefaultForm = ({ setShouldShowSSO, email, setEmail }: Props) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const hasAllFieldsFilled = [firstName, lastName, email, password].every(
    (value) => value.trim().length > 0
  );
  const isPasswordValidLength = Validator.isLength(password, {
    min: PASSWORD_MIN_LENGTH,
    max: PASSWORD_MAX_LENGTH,
  });

  const { mutate, isLoading, isIdle } = useMutation({
    mutationFn: () => {
      const isValidSubmission =
        hasAllFieldsFilled &&
        Validator.isEmail(email) &&
        !usedPersonalEmail(email) &&
        isPasswordValidLength;

      if (!isValidSubmission) throw new Error("Invalid submission");

      return register({
        email,
        password,
        firstName,
        lastName,
      });
    },
    onSuccess: (user) => {
      const signUpEntryPoint = popSignupEntryCookie();
      identify(user, signUpEntryPoint ? { sign_up_entry_point: signUpEntryPoint } : {});

      const originalReferrerId = localStorage.getItem("original_referrer_id");
      localStorage.removeItem("original_referrer_id");

      window.location.href = `/redirect_login${
        originalReferrerId ? `?referrer_id=${originalReferrerId}` : ""
      }`;
    },
    onError: (error) => {
      if (error instanceof Error && error.message === "Invalid submission") {
        return;
      }

      const emailError = (error as AxiosError).response?.data.errors.email[0];
      if (emailError.includes("already been taken")) {
        setErrorMessage("That email has already been taken.");
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
  });

  // e.preventDefault() needs to be outside of mutate in order for it to work properly in Cypress
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutate();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Inputs>
        <SmallInputs>
          <TextInput
            value={firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            placeholder="First name"
            hideClearButton
            data-testid="signup-form-first-name-input"
          />
          <TextInput
            value={lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            placeholder="Last name"
            hideClearButton
            data-testid="signup-form-last-name-input"
          />
        </SmallInputs>
        <TextInput
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          placeholder="Company or university email"
          hideClearButton
          data-testid="signup-form-email-input"
          errors={[
            {
              hasError: !isIdle && !Validator.isEmail(email),
              errorMessage: "Please enter a valid email address.",
            },
            {
              hasError: !isIdle && Validator.isEmail(email) && usedPersonalEmail(email),
              errorMessage: "Please use company or university email.",
            },
          ]}
        />
        <TextInput
          value={password}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          hideClearButton
          data-testid="signup-form-password-input"
          errors={[
            {
              hasError: !isIdle && !isPasswordValidLength,
              errorMessage: "Passwords must be between 8-30 characters long.",
            },
          ]}
        />
      </Inputs>
      <Button
        type="submit"
        variant="marketing"
        disabled={!hasAllFieldsFilled || isLoading}
        width="100%"
        data-testid="signup-form-submit-button"
      >
        Create account
      </Button>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      <LinkButton type="button" onClick={() => setShouldShowSSO(true)}>
        Sign in with SSO
      </LinkButton>
    </Form>
  );
};

export default SignUpModalDefaultForm;

// Used in SignUpModalSSOForm
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;
export const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 140px;
`;
export const ErrorMessage = styled.div`
  color: ${COLORS.RED};
  ${FONTS.MEDIUM_3};
`;
const SmallInputs = styled.div`
  display: flex;
  gap: 16px;
`;
